// The home screen's numbers: which month and which roles they are asked for, and how
// each one is read out of its answer. No React and no network in here, so every rule is
// tested on its own (homeNumbers.test.ts).
//
// Two rules run through the file:
//   1. A NUMBER MUST EQUAL THE ONE ON THE PAGE IT OPENS. Every dashboard opens on the
//      current month, narrowed to the store-level roles. The month string and the
//      names → ids step below are the Dashboard page's own, so the home screen asks the
//      same question the dashboard will ask a moment later.
//   2. A MISSING NUMBER IS NOT ZERO. A reader answers null when the answer does not
//      carry the number; the screen then leaves it out. A 0 is a statement ("nothing is
//      waiting") and is only ever drawn when the server said so.
import { storeLevelRoleNames, type ProjectRoleLite } from '../../navigation/dashboards';

type Dict = Record<string, unknown>;

const dict = (v: unknown): Dict | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Dict) : null);

/** A whole count the answer really carries; null for anything else (absent, blank, negative, text). */
const count = (v: unknown): number | null => {
  // Number(null), Number('') and Number(false) are all 0, so only a real number or a
  // non-blank numeric string is read at all.
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
};

// ── Month and roles: the question every dashboard asks ───────────────────────────

/** 'YYYY-MM-01' for the month the browser is in: the month the Dashboard opens on. */
export function currentPeriodMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

/** The 'YYYY-MM' each dashboard is handed as `month`. */
export const dashboardMonth = (periodMonth: string): string => periodMonth.slice(0, 7);

/** The project's roles as the Dashboard keeps them: id as text, category trimmed. */
export function readProjectRoles(raw: unknown): ProjectRoleLite[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(dict)
    .filter((r): r is Dict => r !== null)
    .map((r) => ({
      id: String(r.id ?? ''),
      name: String(r.name ?? ''),
      category: String(r.category ?? '').trim(),
    }));
}

/**
 * Role NAMES to role ids, in the roles' own order. Trimmed and case-folded on both
 * sides, as the Dashboard does: an untrimmed name in the roles table would otherwise
 * resolve to no id and the number would silently be counted over everyone.
 */
export function roleIdsForNames(roles: ProjectRoleLite[], names: string[]): string[] {
  const want = new Set(names.map((n) => n.trim().toLowerCase()).filter(Boolean));
  return roles.filter((r) => want.has(r.name.trim().toLowerCase())).map((r) => r.id);
}

/**
 * The `roleId` every dashboard starts with: the store-level roles' ids, comma-separated.
 * Undefined means no Role filter, which is what the Dashboard also does when no role
 * qualifies (it fails open to everyone, never to nobody).
 */
export function defaultRoleId(roles: ProjectRoleLite[], intelligence: unknown): string | undefined {
  const names = storeLevelRoleNames(roles, dict(intelligence) as Parameters<typeof storeLevelRoleNames>[1]);
  return roleIdsForNames(roles, names).join(',') || undefined;
}

// ── Needs attention ──────────────────────────────────────────────────────────────

export interface OpenAppeals {
  count: number;
  /** Whole days the oldest one has waited; null when no row carried an age. */
  oldestDays: number | null;
  /** India-time day ('YYYY-MM-DD') the oldest one was raised; null unless EVERY row carried a readable date. */
  oldestRaisedOn: string | null;
}

// India-time calendar day of a timestamp, read exactly the way the appeals lists read
// "Raised" (a plain `new Date`, on the server's raw timestamp text). That is on purpose:
// a browser that cannot read the text gives null here, and the list is then not handed
// a "Raised" window it could not apply either.
const istDay = (iso: unknown): string | null => {
  if (typeof iso !== 'string' || !iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

/** Today in India time, as 'YYYY-MM-DD'. */
export const todayInIndia = (now: Date = new Date()): string =>
  now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const DAY_MS = 86_400_000;
const UNDECIDED_STATUSES = new Set(['pending', 'under_review']);

/**
 * The appeals on one appeals LIST that nobody has decided yet, from that list's own
 * rows. `lists` is one array of rows per answer (the list is asked once per undecided
 * status); null when any of them is not an array.
 *
 * Counted from the list rather than from the Appeals dashboard on purpose. The
 * dashboard counts over the people in the viewer's roster; the list shows every appeal
 * filed in the project. An appeal by someone who has since left is on the list and not
 * in the dashboard's count, so only the list's own rows always agree with the list.
 *
 * An appeal is counted once however many answers carry it (it can move from pending to
 * under review between the two requests), and only while its status is undecided.
 */
export function readOpenAppeals(lists: unknown[], now: Date = new Date()): OpenAppeals | null {
  if (lists.length === 0 || !lists.every(Array.isArray)) return null;
  const seen = new Set<string>();
  const raised: Array<string | null> = [];
  let oldestDays: number | null = null;
  for (const raw of (lists as unknown[][]).flat()) {
    const row = dict(raw);
    if (!row || !UNDECIDED_STATUSES.has(String(row.status))) continue;
    // The red list calls it appealId, the yellow list id.
    const id = row.appealId ?? row.id;
    if (id != null) {
      if (seen.has(String(id))) continue;
      seen.add(String(id));
    }
    raised.push(istDay(row.createdAt));
    const at = typeof row.createdAt === 'string' ? new Date(row.createdAt).getTime() : NaN;
    if (Number.isFinite(at)) {
      const days = Math.max(0, Math.floor((now.getTime() - at) / DAY_MS));
      if (oldestDays === null || days > oldestDays) oldestDays = days;
    }
  }
  // One unreadable date and the earliest of the rest no longer bounds them all.
  const days = raised.length > 0 && raised.every((d): d is string => d !== null) ? [...raised].sort() : null;
  return { count: raised.length, oldestDays, oldestRaisedOn: days ? days[0] : null };
}

/**
 * What the Notice period tab's count says to the person on the home screen: the number of
 * people serving notice whose hiring is not raised yet, or that the tab can show them no
 * one to act on (`'cannot-act'`).
 */
export type NoticeNotRaised = number | 'cannot-act';

/**
 * People serving notice whose hiring is not raised yet, off the Hiring dashboard's Notice
 * period tab's count (GET .../notice/count). Only when 1Office was read whole for it: a
 * read that says some people could not be checked, or that 1Office could not be read at
 * all, has no number here (null: a short count would read as the whole).
 *
 * `'cannot-act'` when the person has no 1Office account to read with (`no_identity`): the
 * tab then lists nobody for them, so it shows no row to raise, ask for or link hiring on,
 * and the home screen offers no card for it. (Not an outage: that is `unavailable`, null.)
 */
export function readNoticeNotRaised(data: unknown): NoticeNotRaised | null {
  const d = dict(data);
  const read = dict(d?.oneOffice)?.read;
  if (read === 'no_identity') return 'cannot-act';
  if (read !== 'read') return null;
  return count(d?.notRaised);
}

/** The rows of a Red Flags Appeals answer ({ rows }); the Yellow Flag Appeals answer is the array itself. */
export const redAppealRows = (data: unknown): unknown => dict(data)?.rows;

/** "Oldest has waited 8 days", worded for one appeal or several; null when no age is known. */
export function waitedLine(appeals: OpenAppeals): string | null {
  const days = appeals.oldestDays;
  if (days === null || appeals.count === 0) return null;
  const one = appeals.count === 1;
  if (days === 0) return one ? 'Raised today' : 'Oldest was raised today';
  const waited = `waited ${days} day${days === 1 ? '' : 's'}`;
  return one ? `Has ${waited}` : `Oldest has ${waited}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "9:40 am", on the browser's own clock: the one the person can compare it with. A time
 * that is not from today carries its day ("11 Oct, 9:40 pm"), or it would read as today's.
 */
export function clockTime(at: Date, now: Date = new Date()): string {
  const hour = at.getHours();
  const time = `${hour % 12 || 12}:${String(at.getMinutes()).padStart(2, '0')} ${hour < 12 ? 'am' : 'pm'}`;
  return at.toDateString() === now.toDateString() ? time : `${at.getDate()} ${MONTHS[at.getMonth()]}, ${time}`;
}

/** People on Start Exit's "Pending review" tab. */
export interface ExitReviews {
  count: number;
  /** When it was counted, as words; null while the count is fresh, or carries no readable time. */
  asOf: string | null;
}

// Start Exit's list is always worked out live; this count is kept by the server for two
// minutes. A decision taken on the page drops the kept answer at once, so what a kept
// answer can still miss is a change made anywhere else. Past this age the row says when
// it was taken. Well under the two minutes on purpose: at two minutes the note would
// print only on an answer the server was about to replace, which is almost never.
const EXIT_COUNT_FRESH_MS = 30_000;

/**
 * The exit-review count and, when it is more than half a minute old, the time it was
 * taken. `now` is the moment the answer is read, so the age is the one it arrived with.
 *
 * The age is the browser's clock against the server's stamp, and the two never agree
 * to the second. A stamp ahead of this clock gives a negative age, which is simply not
 * old: no note is printed for an answer "from the future". A clock that runs fast only
 * prints the note a little early, and the time it names is still the right one.
 */
export function readExitReviews(data: unknown, now: Date = new Date()): ExitReviews | null {
  const d = dict(data);
  const pending = count(d?.pending);
  if (pending === null) return null;
  const at = typeof d?.asOf === 'string' && d.asOf ? new Date(d.asOf) : null;
  const old = at !== null && !Number.isNaN(at.getTime()) && now.getTime() - at.getTime() > EXIT_COUNT_FRESH_MS;
  return { count: pending, asOf: old ? clockTime(at, now) : null };
}

/** Improvement plans that ended without improvement and wait for Extend or Terminate. */
export const readPlansToDecide = (data: unknown): number | null => count(dict(data)?.count);

/** The person's own probation confirmations, across every project. */
export interface ProbationWaiting {
  waiting: number;
  /** Those past the probation end date; null when the answer did not say. */
  overdue: number | null;
}

export function readProbationWaiting(data: unknown): ProbationWaiting | null {
  const d = dict(data);
  const waiting = count(d?.waiting);
  return waiting === null ? null : { waiting, overdue: count(d?.overdue) };
}

/** New joiners whose onboarding plan is running and who have no buddy. */
export const readNoBuddy = (data: unknown): number | null => count(dict(data)?.noBuddy);

/** New stores requested from the mobile app and not yet approved or rejected. */
export const readPendingStoreRequests = (data: unknown): number | null => count(dict(data)?.pending);

/** Reported posts still open: one row per reported post or comment, however many people reported it. */
export const readOpenReported = (data: unknown): number | null => (Array.isArray(data) ? data.length : null);

/** Rows on the Social Moderation page's AI review tab that still wait for a moderator, from any date. */
export const readNeedsReview = (data: unknown): number | null => count(dict(dict(data)?.counts)?.needsReview);

/**
 * The window the Social Moderation page opens on: the last 30 days, in the browser's
 * own calendar. The reported count is asked for the same window, so it equals the
 * number of rows the page lists when it opens.
 */
export function moderationWindow(now: Date = new Date()): { from: string; to: string } {
  const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const from = new Date(now);
  from.setDate(from.getDate() - 30);
  return { from: day(from), to: day(now) };
}

/** The two worklists on the Social Moderation page. A part is null when its call was refused or failed. */
export interface PostsWaiting {
  fromCheck: number | null;
  reported: number | null;
}

/**
 * How many posts wait, from the parts that arrived. Zero only when BOTH lists said
 * zero: with one part missing and nothing in the other, the truth is not known.
 */
export function postsWaitingTotal(posts: PostsWaiting): number | null {
  const known = (posts.fromCheck ?? 0) + (posts.reported ?? 0);
  if (known > 0) return known;
  return posts.fromCheck !== null && posts.reported !== null ? 0 : null;
}

/**
 * Where the waiting posts are; names only the parts that arrived. The first part is
 * named as the Social Moderation page names its tab, so the person knows where to look.
 */
export function postsWaitingLine(posts: PostsWaiting): string | null {
  const parts: string[] = [];
  if (posts.fromCheck !== null) parts.push(`${posts.fromCheck} from AI review`);
  if (posts.reported !== null) parts.push(`${posts.reported} reported`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ── Dashboards ───────────────────────────────────────────────────────────────────

/** Employees red-flagged today: one row per employee on the dashboard's "Red Flags Today". */
export function readFlaggedToday(data: unknown): number | null {
  const d = dict(data);
  return Array.isArray(d?.rows) ? d.rows.length : count(d?.total);
}

/** Yellow flags raised this month: the total printed above the dashboard's trend. */
export const readYellowRaised = (data: unknown): number | null => count(dict(data)?.total);

/** People on this month's low-mood watchlist (unhappy on two or more days). */
export function readLowMood(data: unknown): number | null {
  const list = dict(dict(data)?.lowMood);
  return count(list?.total) ?? (Array.isArray(list?.rows) ? list.rows.length : null);
}

/** Uniform requests that went past the 3-day target this month; null before the measure's go-live. */
export function readUniformPastTarget(data: unknown): number | null {
  const d = dict(data);
  // Strictly `true`, as the dashboard reads it: an unmeasured month has no counts at all.
  return d?.measured === true ? count(d.out) : null;
}

/**
 * Training invitations this month with attendance not marked: one per person per
 * session that has started and has neither a Yes nor a No. The Training dashboard's
 * "Not marked" figure.
 */
export const readTrainingNotMarked = (data: unknown): number | null => count(dict(data)?.notMarked);

/**
 * Stores with something to fix: the number on the Data Hygiene dashboard's Stores
 * switch. The server answers null for someone who does not see the whole project (that
 * dashboard has nothing to show them), and that is no number here either.
 */
export const readStoresToFix = (data: unknown): number | null => count(dict(data)?.stores);

/**
 * People who left in the month: the "Exits" of that month on the Attrition dashboard,
 * for this person and the dashboard's Role filter. The server answers null where the
 * dashboard draws no figure (nobody reports to them, or the exit records look missing),
 * and that is no number here either.
 */
export const readAttritionExits = (data: unknown): number | null => count(dict(data)?.exits);

// ── Kept in the background ───────────────────────────────────────────────────────
// Four numbers are too heavy to work out while the home screen waits (each reads a whole
// project from 1Office), so the server reads them in the background and a count only
// narrows what it has kept to the person asking. What it has kept can be several minutes
// behind the list, so each of these numbers is shown WITH the time its facts were read,
// and in words that say the past ("had not checked in").

/** A number the server keeps in the background. */
export interface KeptCount {
  count: number;
  /** When its facts were read, as words ("10:40 am"). Never missing: a kept number is not shown without it. */
  asOf: string;
}

/**
 * What a kept count's answer says: the number; `'not-yet'` when the server holds
 * nothing yet (no number AND no time: it has started reading, so the number is worth
 * asking for again shortly); or null when the answer carries no number for this person
 * and asking again would not change that.
 */
export type Kept<T> = T | 'not-yet' | null;

function readKept(data: unknown, field: string, now: Date): Kept<KeptCount> {
  const d = dict(data);
  if (!d) return null;
  // Both missing, exactly: a number without its time is not shown, and a time without a
  // number is the server saying there is none for this person.
  if (d[field] == null && d.asOf == null) return 'not-yet';
  const n = count(d[field]);
  const at = typeof d.asOf === 'string' && d.asOf ? new Date(d.asOf) : null;
  if (n === null || at === null || Number.isNaN(at.getTime())) return null;
  return { count: n, asOf: clockTime(at, now) };
}

/** Requests on the Leave & Attendance Requests dashboard's Pending list: raised this month, still undecided. */
export interface RequestsPending extends KeptCount {
  /** Whole days the oldest one has waited; null when the answer did not say. */
  oldestWaitDays: number | null;
}

export function readRequestsPending(data: unknown, now: Date = new Date()): Kept<RequestsPending> {
  const kept = readKept(data, 'pending', now);
  return kept === null || kept === 'not-yet' ? kept : { ...kept, oldestWaitDays: count(dict(data)?.oldestWaitDays) };
}

/** Why the server does not count My Approvals for this person, as it says it. */
export type ApprovalsNotCounted = 'outside-project';

/**
 * What waits on My Approvals for this person, or the server's definite word that it is
 * not counted here (`notCounted`): their people reach beyond the project it read, so a
 * number would be short of the page, and asking again would find the same.
 */
export type ApprovalsPending = RequestsPending | { notCounted: ApprovalsNotCounted };

/**
 * What waits on My Approvals for this person (leave, attendance corrections and comp-off
 * claims the page lists as Pending): the same answer as the requests count, from the
 * server's own count of that page; or `{ notCounted }` when the server answers no number,
 * the time of its read and `notCounted: 'outside_project'` (a definite answer, not "not yet").
 */
export function readApprovalsPending(data: unknown, now: Date = new Date()): Kept<ApprovalsPending> {
  const d = dict(data);
  if (d && d.pending == null && typeof d.asOf === 'string' && d.asOf && d.notCounted === 'outside_project') {
    return { notCounted: 'outside-project' };
  }
  return readRequestsPending(data, now);
}

/** People on the Attendance dashboard's Absent Today list: shift start had passed, no check-in, no leave. */
export const readAbsentToday = (data: unknown, now: Date = new Date()): Kept<KeptCount> => readKept(data, 'absentToday', now);

/** Stores on the Deployment dashboard's Undeployed Stores list: nobody, or fewer people than the category needs. */
export const readStoresShort = (data: unknown, now: Date = new Date()): Kept<KeptCount> => readKept(data, 'short', now);

/**
 * Appeals nobody has decided yet, as the Appeals dashboard shows them: its "Open
 * red-flag" and "Open yellow-flag" tiles added together. The two tiles split the rows of
 * the dashboard's open queue by kind, so together they are every row of it.
 *
 * Not the same people as the two appeals cards under "Needs attention" (see
 * readOpenAppeals): this one is counted over the dashboard's roster and Role filter.
 *
 * The answer also says how many rows the queue holds. If that ever disagrees with the
 * rows sent, the rows are not known to be the whole queue, so there is no number.
 */
export function readAppealsOpen(data: unknown): number | null {
  const queue = dict(dict(data)?.openQueue);
  if (!queue || !Array.isArray(queue.rows)) return null;
  return count(queue.total) === queue.rows.length ? queue.rows.length : null;
}

// ── Built by your team ───────────────────────────────────────────────────────────

export interface TeamDashboard {
  id: number;
  title: string;
  description: string | null;
  /** Employee code of whoever built it, trimmed and lower-cased for comparing. */
  createdBy: string;
}

export interface TeamDashboards {
  /** The person may build a dashboard in this project. */
  canAuthor: boolean;
  published: TeamDashboard[];
  /** Not published yet. The server sends a draft only to its builder and to people who may build. */
  drafts: TeamDashboard[];
}

/** The two answers the Dashboard has always asked for (access, list), in the server's order. */
export function readTeamDashboards(access: unknown, list: unknown): TeamDashboards {
  const rows = dict(list)?.dashboards;
  const out: TeamDashboards = { canAuthor: Boolean(dict(access)?.canAuthor), published: [], drafts: [] };
  for (const raw of Array.isArray(rows) ? rows : []) {
    const row = dict(raw);
    const id = Number(row?.id);
    if (!row || !Number.isFinite(id)) continue;
    const dashboard: TeamDashboard = {
      id,
      title: String(row.title ?? '').trim() || 'Untitled dashboard',
      description: typeof row.description === 'string' && row.description.trim() ? row.description : null,
      createdBy: String(row.created_by_emp_code ?? '').trim().toLowerCase(),
    };
    // Anything not published is drawn as a draft, as the Dashboard's own strip did.
    (row.status === 'published' ? out.published : out.drafts).push(dashboard);
  }
  return out;
}

// ── Words ────────────────────────────────────────────────────────────────────────

/** Indian digit grouping, as the dashboards print counts. */
export const fmtNumber = (n: number): string => n.toLocaleString('en-IN');
